import csiAction from "csi/action";
import comm from "csi/comm";
import { EMPTY_URL } from "csi/constants";
import stateColorResolver from "csi/heading/stateColorResolver";
import locale from "csi/locale";
import csi from "csi/main";
import PageStore from "csi/page/PageStore";
import reflection from "csi/reflection";
import { getAncestorNodes, resolveNodesWithDependencies } from "csi/service/nodeService";
import session from "csi/session";
import WorkingCache from "csi/state/WorkingCache";
import { rootStore, routerStore } from "csi/state/rootStore";
import { TableWidget } from "csi/table/Widget";
import { C8Node, ICache, cnl } from "csi/typings";
import { LOCATION_COMPARE_URL, LOCATION_REVISION_URL, LOCATION_URL } from "csi/utility/routerUtility";
import csiView from "csi/view";
import xml from "csi/xml";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable, override, runInAction } from "mobx";
import { createContext, useContext } from "react";
import ApparelBom from "src/Specification/Model/ApparelBom";

export const ACTION_REVISION_CURRENT_SAMPLE = "BreadcrumbRevisionCurrentSample";
export const ACTION_REVISION_LATEST = "BreadcrumbRevisionLatest";
export const ACTION_REVISION_NEXT = "BreadcrumbRevisionNext";
export const ACTION_REVISION_PREV = "BreadcrumbRevisionPrev";
export const ACTION_REVISION_ALL = "BreadcrumbRevisionAll";
export const COMPARE_BOM = "BomCompare";

class ComparePageStore extends PageStore {
	currentRevisionUrl: cnl;
	@observable compareRevisionUrl: cnl;

	compareProductAncestorUrls: cnl[];

	/** flag to indicate readonly mode */
	@observable readonlyMode = false;

	/** extension point to handle readonly mode change */
	protected readonly _onChangeReadOnlyMode: () => void;

	@computed get isCompareBomSelectEnabled(): boolean {
		return !this.readonlyMode;
	}

	@computed get isCurrentBomSelectEnabled(): boolean {
		return !this.readonlyMode;
	}
	
	get bomCompareMenuItems(): string[] {
		const { cache, currentNode } = this;
		if (currentNode && currentNode.$Type !== "ApparelBOM") {
			const response = [];
			response.push(COMPARE_BOM);
			return response;
		}
		return ApparelBom.bomCompareMenuItems(currentNode, cache);
	}

	get compareNode(): C8Node {
		return this.cache.get(this.compareRevisionNode?.__Parent__);
	}

	get compareNodeHasOneRevision(): boolean {
		return this.compareNode.Revisions.length === 1;
	}

	get comparePDF(): string | null {
		return this.getPDF(this.compareNode, this.compareRevisionNode);
	}

	get currentPDF(): string | null {
		return this.getPDF(this.currentNode, this.currentRevisionNode);
	}

	get comparePhaseNode(): C8Node {
		return reflection.getNode(this.compareRevisionNode.Phase);
	}

	get comparePhaseNodes(): C8Node[] | null {
		const compareClassConfig = reflection.getClassConfig(this.compareNode.$Type);
		return compareClassConfig?.getPhases();
	}

	get compareProductAncestorNodes(): C8Node[] {
		return this.cache.getNodes(this.compareProductAncestorUrls);
	}

	get compareProductNode(): C8Node {
		return this.cache.get(this.compareNode?.__Parent__);
	}

	get compareRevisionNode(): C8Node {
		return this.cache.get(this.compareRevisionUrl);
	}

	get currentNode(): C8Node {
		return routerStore.locationNode;
	}

	get currentNodeHasOneRevision(): boolean {
		return this.currentNode.Revisions.length === 1;
	}

	get currentPhaseNode(): C8Node {
		return reflection.getNode(this.currentRevisionNode.Phase);
	}

	get currentPhaseNodes(): C8Node[] | null {
		const currentClassConfig = reflection.getClassConfig(this.currentNode.$Type);
		return currentClassConfig?.getPhases();
	}

	get currentProductNode(): C8Node {
		return this.cache.get(this.currentNode?.__Parent__);
	}

	get currentProductAncestorNodes(): C8Node[] {
		return getAncestorNodes(this.currentProductNode.$URL, this.cache).reverse();
	}

	get currentRevisionNode(): C8Node {
		return routerStore.locationRevisionNode;
	}

	get isCompareMode(): boolean {
		return xml.isValidURL(this.compareRevisionUrl);
	}

	get isCompareSampleBOM(): boolean {
		return this.compareNode.IsSampleBOM;
	}

	get isCurrentSampleBOM(): boolean {
		return this.currentNode.IsSampleBOM;
	}


	@computed get showEditButton(): boolean {
		return this.readonlyMode;
	}

	/** action to set compare url */
	setCompareRevisionUrl = async (url: cnl): Promise<boolean> => {
		const { cache, compareRevisionUrl } = this;

		const [node] = await resolveNodesWithDependencies([url], [reflection.PATH_PARENT], cache);

		if (node) {
			const request = comm.getBreadcrumbRequest(node.__Parent__, url);
			const response = await request.go();
			cache.cacheResponse(response);
			this.compareProductAncestorUrls = response.getResultUrls().reverse();
		}

		// it's possible url in browser location is invalid, so only change if valid node
		if (!cache.get(url)) {
			url = EMPTY_URL;
		}

		if (url !== compareRevisionUrl) {
			runInAction(() => {
				this.compareRevisionUrl = url;
				session.setLocationActual(LOCATION_COMPARE_URL, url, null, this.onChangeCompareUrl, true);
			});

			return true;
		}

		return false;
	};

	/** compare with either another BOM or another revision */
	compareWith(pluginName: string): void {
		const view = rootStore.layoutStore?.selectedItem?.view as TableWidget;
		if (view) {
			const materialsView = view.getChildView("PartMaterials");
			const multiLevelMaterialsView = view.getChildView("HierarchicalPartMaterials");
			const plugin = view.getPlugin(pluginName) || materialsView?.getPlugin(pluginName) || multiLevelMaterialsView?.getPlugin(pluginName);
			if (plugin) {
				csiAction.invoke(view, view.domNode, plugin.defAction);
			}
		}
	}

	/** un-hide breadcrumb, set location key to centric: */
	endCompareMode = (): void => {
		// need to register new url in browser history
		session.setLocationKey(LOCATION_COMPARE_URL, EMPTY_URL);
	};

	/** get chip state color class from revision node */
	getChipStateColorClass(revisionNode: C8Node): string {
		return stateColorResolver.getStateCss(revisionNode);
	}

	/** get chip state label from revision node */
	getChipStateLabel(revisionNode: C8Node): string {
		return locale.capitalizeResource(revisionNode.State);
	}

	/** get pdf href link */
	getPDF(bomNode: C8Node, revisionNode: C8Node): string {
		return comm.getFileHref(revisionNode.PDF, false, locale.getNodeName(bomNode) + (revisionNode.$Name ? (" - " + revisionNode.$Name) : ""));
	}

	/** onChange listener for compare key */
	onChangeCompareUrl = async (url: cnl): Promise<void> => {
		// used when location compare url changes
		const changed = await this.setCompareRevisionUrl(url);
		if (changed) {
			this.refreshViewIfExists();
		}
	};

	/** selecting single revision */
	onSelectRevision = (revisionUrl: cnl, isCompareMenu: boolean): void => {
		session.setLocationKey(isCompareMenu ? LOCATION_COMPARE_URL : LOCATION_REVISION_URL, revisionUrl, true);
	};

	/** selecting all revisions */
	onSelectRevisionAll = (bomUrl: cnl, isCompareMenu: boolean): void => {
		const view = csiView.getView("__Breadcrumb__", routerStore.locationUrl);
		const action = view.getAction(ACTION_REVISION_ALL);
		action.m_viewRef.m_url = bomUrl;
		action.m_tooltip = locale.resource("BomCompareRevision");
		if (isCompareMenu) {
			csiAction.invoke(view, view.domNode, action, { compare: true });
		} else {
			csiAction.invoke(view, view.domNode, action);
		}
	};

	/** Change to readonly mode by default if no extension point is provided */
	@action onChangeReadOnlyMode = (): void => {
		if (this._onChangeReadOnlyMode) {
			this._onChangeReadOnlyMode();
		} else {
			this.readonlyMode = !this.readonlyMode;
		}
	};

	/** get compare pdf href link */
	openComparePDF = (): void => {
		csi.browserWindow(this.comparePDF);
	};

	/** get current pdf href link */
	openCurrentPDF = (): void => {
		csi.browserWindow(this.currentPDF);
	};

	/** change bom selection */
	onChangeBOM = (item: ComponentItem, isCompareMenu: boolean): void => {
		this._onChangeBOM(item, isCompareMenu);
	};

	/** change bom selection */
	_onChangeBOM(item: ComponentItem, isCompareMenu: boolean): void {
		const { node } = item;
		if (isCompareMenu) {
			session.setLocationKey(LOCATION_COMPARE_URL, node.LatestRevision, true);
		} else {
			session.setLocationKey(LOCATION_URL, node.$URL, true);
		}
	};

	/** refresh the view if it exists */
	refreshViewIfExists(): void {
		const view = rootStore?.layoutStore?.selectedItem?.view;
		if (view) {
			view.refreshWidget();
		}
	}

	/** override create extension point to do initialization */
	@override onPageStoreCreate(): void {
		session.reserveLocationKey(LOCATION_COMPARE_URL, this.onChangeCompareUrl);
		this.setCompareRevisionUrl(routerStore.compareRevisionUrl);
	}

	/** override create extension point to do cleanup */
	@override onPageStoreDestroy(): void {
		session.releaseLocationKey(LOCATION_COMPARE_URL);
	}

	constructor(node: C8Node, revisionNode: C8Node, cache: ICache = new WorkingCache()) {
		super(node, cache);
		makeObservable(this);
	}
}

export default ComparePageStore;

export const CompareStoreContext = createContext<ComparePageStore>(undefined);
export const useCompareStore = (): ComparePageStore => useContext(CompareStoreContext);